import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import { useTheme, alpha } from "@mui/material/styles";
import {
  DayByDayChoiceBlock,
  WeeklyGridChoiceBlock,
  ChoiceOrDivider,
} from "./AddDietPlanChoiceOptions";

/**
 * Chooses between classic day-by-day creation and weekly grid (per-day range) flow.
 *
 * @param {boolean} isDialogOpen — Whether the dialog is visible.
 * @param {() => void} onCloseDialog — Closes without starting a flow (Cancel / backdrop).
 * @param {() => void} onSelectDayByDay — Runs the same logic as the former primary “Add New Diet Plan” action.
 * @param {() => void} onSelectWeeklyGrid — Runs the same logic as the former “Weekly (per-day)” action.
 */
export default function AddDietPlanChoiceDialog({
  isDialogOpen,
  onCloseDialog,
  onSelectDayByDay,
  onSelectWeeklyGrid,
}) {
  const theme = useTheme();
  const navy = theme.palette.dark.main;
  const muted = theme.palette.text?.main ?? "#67748e";
  const borderSoft = alpha(navy, 0.12);

  return (
    <Dialog
      open={isDialogOpen}
      onClose={onCloseDialog}
      fullWidth
      maxWidth="sm"
      aria-labelledby="add-diet-plan-choice-title"
      aria-describedby="add-diet-plan-choice-desc"
      scroll="body"
      PaperProps={{
        elevation: 8,
        sx: {
          borderRadius: 2,
          overflow: "hidden",
          width: { xs: "calc(100% - 24px)", sm: "100%" },
          maxWidth: { xs: 400, sm: 480 },
          m: { xs: 1.5, sm: 2 },
          boxShadow: `0 12px 40px ${alpha(navy, 0.12)}`,
        },
      }}
    >
      <DialogTitle
        id="add-diet-plan-choice-title"
        sx={{
          pt: 3,
          px: 3,
          pb: 1,
          fontSize: { xs: "1.1rem", sm: "1.25rem" },
          fontWeight: 700,
          lineHeight: 1.35,
          color: navy,
          letterSpacing: "-0.01em",
        }}
      >
        How would you like to create this plan?
      </DialogTitle>

      <DialogContent sx={{ px: 3, pt: 0, pb: 2 }}>
        <Typography
          id="add-diet-plan-choice-desc"
          variant="body2"
          sx={{
            color: muted,
            lineHeight: 1.6,
            mb: 2.5,
            fontSize: "0.875rem",
          }}
        >
          Choose whether to build the full week in a grid, or add one day at a time.
        </Typography>

        <WeeklyGridChoiceBlock
          navy={navy}
          muted={muted}
          theme={theme}
          onSelect={onSelectWeeklyGrid}
        />
        <ChoiceOrDivider navy={navy} muted={muted} />
        <DayByDayChoiceBlock
          navy={navy}
          muted={muted}
          theme={theme}
          onSelect={onSelectDayByDay}
        />
      </DialogContent>

      <DialogActions
        sx={{
          px: 3,
          py: 1.5,
          pt: 0,
          justifyContent: "flex-end",
          borderTop: `1px solid ${borderSoft}`,
          bgcolor: alpha(navy, 0.02),
        }}
      >
        <Button
          onClick={onCloseDialog}
          color="info"
          sx={{
            textTransform: "uppercase",
            fontWeight: 600,
            fontSize: "0.75rem",
            letterSpacing: "0.08em",
            px: 1.5,
            py: 0.75,
            borderRadius: 1,
            "&:focus-visible": {
              outline: `2px solid ${theme.palette.info.main}`,
              outlineOffset: 2,
            },
          }}
        >
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
}
